/**
 * Edit one playbook parameter: its thresholds, how seriously silence is
 * treated, its priority, and the wording shown next to findings.
 */

import { createPortal } from 'react-dom';
import React, { useEffect, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import {
  ArgField,
  Classification,
  PlaybookOverrideFields,
  PlaybookParameterRow,
  PriorityTier
} from '../../../services/api/ruleService';
import { CLASSIFICATION_LABELS, TIER_LABELS, unitLabel } from './ruleBookConstants';

interface ParameterEditModalProps {
  parameter: PlaybookParameterRow;
  saving: boolean;
  error?: string | null;
  onSave: (fields: PlaybookOverrideFields & { version?: number }) => void;
  onClose: () => void;
}

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400';

const CLASSES: Classification[] = ['ACCEPTABLE', 'NEGOTIATE', 'HIGH_RISK', 'CRITICAL'];

const ClassSelect: React.FC<{ id?: string; value: Classification; onChange: (c: Classification) => void }> = ({ id, value, onChange }) => (
  <select id={id} className={`${inputClass} pr-9`} value={value} onChange={e => onChange(e.target.value as Classification)}>
    {CLASSES.map(c => <option key={c} value={c}>{CLASSIFICATION_LABELS[c]}</option>)}
  </select>
);

const TEXT_FIELDS: { key: 'preferredText' | 'fallbackText' | 'redFlagText' | 'challenge'; label: string }[] = [
  { key: 'preferredText', label: 'Preferred position' },
  { key: 'fallbackText', label: 'Fallback position' },
  { key: 'redFlagText', label: 'Red flag' },
  { key: 'challenge', label: 'Challenge to raise' }
];

export const ParameterEditModal: React.FC<ParameterEditModalProps> = ({ parameter, saving, error, onSave, onClose }) => {
  const { defaults, override } = parameter;

  // The form always holds full effective values; the server stores only what differs from the defaults
  const initial = () => ({
    enabled: override?.enabled ?? defaults.enabled,
    priorityTier: override?.priorityTier ?? defaults.priorityTier,
    whenAbsent: override?.whenAbsent ?? defaults.whenAbsent,
    args: { ...defaults.args, ...(override?.args || {}) },
    preferredText: override?.preferredText ?? defaults.preferredText,
    fallbackText: override?.fallbackText ?? defaults.fallbackText,
    redFlagText: override?.redFlagText ?? defaults.redFlagText,
    challenge: override?.challenge ?? defaults.challenge
  });
  const [form, setForm] = useState(initial);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setForm(initial()), [parameter]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const setArg = (name: string, value: unknown) =>
    setForm(f => ({ ...f, args: { ...f.args, [name]: value } }));

  const unit = unitLabel(parameter.extraction.unit);

  const renderArg = (field: ArgField) => {
    const id = `arg-${field.name}`;
    const value = form.args[field.name];
    const isDefault = JSON.stringify(value ?? null) === JSON.stringify(defaults.args[field.name] ?? null);

    let control: React.ReactNode;
    if (field.type === 'number') {
      control = (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="number"
            min={0}
            step="any"
            className={inputClass}
            value={value ?? ''}
            placeholder={field.optional ? 'Not set' : ''}
            onChange={e => setArg(field.name, e.target.value === '' ? (field.optional ? null : undefined) : Number(e.target.value))}
          />
          {unit && <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">{unit}</span>}
        </div>
      );
    } else if (field.type === 'classification') {
      control = <ClassSelect id={id} value={value} onChange={c => setArg(field.name, c)} />;
    } else {
      const mapping: Record<string, Classification> = value || {};
      control = (
        <div className="rounded-lg border border-gray-200 dark:border-dark-border divide-y divide-gray-100 dark:divide-dark-border">
          {Object.keys(defaults.args.mapping || {}).map(option => (
            <div key={option} className="flex items-center gap-3 px-3 py-2">
              <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{option}</span>
              <div className="w-44">
                <ClassSelect value={mapping[option]} onChange={c => setArg(field.name, { ...mapping, [option]: c })} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div key={field.name}>
        <label htmlFor={field.type === 'optionMap' ? undefined : id} className="flex items-baseline justify-between mb-1.5">
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{field.label}</span>
          {!isDefault && (
            <button type="button" onClick={() => setArg(field.name, defaults.args[field.name])} className="text-xs text-primary-600 dark:text-accent-400 hover:underline">
              Default: {formatDefault(defaults.args[field.name], unit)}
            </button>
          )}
        </label>
        {control}
        {field.help && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.help}</p>}
      </div>
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const fields: PlaybookOverrideFields & { version?: number } = {
      enabled: form.enabled,
      priorityTier: form.priorityTier as PriorityTier,
      whenAbsent: form.whenAbsent,
      preferredText: form.preferredText,
      fallbackText: form.fallbackText,
      redFlagText: form.redFlagText,
      challenge: form.challenge,
      version: override?.version
    };
    if (parameter.evaluator.editable) fields.args = form.args;
    onSave(fields);
  };

  // Portalled to <body>: rendered in place, the overlay picks up layout from its
  // parent (e.g. a space-y margin) and stops covering the whole window.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && onClose()}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="param-edit-title"
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-dark-surface shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100 dark:border-dark-border">
          <div className="min-w-0">
            <h2 id="param-edit-title" className="text-lg font-semibold text-gray-900 dark:text-white">{parameter.label}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{parameter.topic}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={form.enabled}
              onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
            />
            <span>
              <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">Check this parameter</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                When off, the AI doesn't look for it and it never appears in a review.
              </span>
            </span>
          </label>

          {form.enabled && (
            <>
              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Thresholds</h3>
                {parameter.evaluator.editable
                  ? parameter.evaluator.fields.map(renderArg)
                  : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {parameter.contextOnly
                        ? 'This parameter is only used together with other clauses and has no threshold of its own.'
                        : 'This check uses fixed logic that reads the clause wording, so it has no adjustable threshold.'}
                    </p>
                  )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="when-absent" className="block mb-1.5 text-sm font-medium text-gray-800 dark:text-gray-200">If the contract is silent</label>
                    <ClassSelect id="when-absent" value={form.whenAbsent} onChange={c => setForm(f => ({ ...f, whenAbsent: c }))} />
                  </div>
                  <div>
                    <label htmlFor="priority-tier" className="block mb-1.5 text-sm font-medium text-gray-800 dark:text-gray-200">Negotiation priority</label>
                    <select
                      id="priority-tier"
                      className={`${inputClass} pr-9`}
                      value={form.priorityTier}
                      onChange={e => setForm(f => ({ ...f, priorityTier: Number(e.target.value) as PriorityTier }))}
                    >
                      {([1, 2, 3, 4] as PriorityTier[]).map(t => <option key={t} value={t}>Tier {t}: {TIER_LABELS[t]}</option>)}
                    </select>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Wording shown in reviews</h3>
                {TEXT_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label htmlFor={`text-${key}`} className="flex items-baseline justify-between mb-1.5">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</span>
                      {form[key] !== defaults[key] && (
                        <button type="button" onClick={() => setForm(f => ({ ...f, [key]: defaults[key] }))} className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-accent-400 hover:underline">
                          <RotateCcw className="w-3 h-3" /> Restore default
                        </button>
                      )}
                    </label>
                    <textarea
                      id={`text-${key}`}
                      className={`${inputClass} min-h-[72px]`}
                      maxLength={2000}
                      value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-border">
          {/* Errors go in the footer, beside the button that caused them: at the end of the
              scrolling body they'd be out of sight and Save would look like it did nothing */}
          {error ? (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Changes apply to the next analysis that runs.</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:hover:bg-accent-400 dark:text-gray-900 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body
  );
};

const formatDefault = (value: unknown, unit: string): string => {
  if (value === null || value === undefined) return 'not set';
  if (typeof value === 'number') return unit ? `${value} ${unit}` : String(value);
  if (typeof value === 'string') return CLASSIFICATION_LABELS[value as Classification] ?? value;
  return 'restore';
};

export default ParameterEditModal;
